import { styled } from "@linaria/react";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { XIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import AvatarShimmer from "../avatar/AvatarShimmer";
import FlexItem from "../containers/FlexItem";
import FlexWrapper from "../containers/FlexWrapper";
import Icon, { IconVariant } from "../icons/Icon";
import Ellipsis from "../text/Ellipsis";
import Text, { TextSize, TextVariant } from "../text/Text";
import TextShimmer from "../text/TextShimmer";

export enum ChipVariant {
  PRIMARY = "PRIMARY",
  PRIMARY_ALT = "PRIMARY_ALT",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
  SUCCESS = "SUCCESS",
  WARNING = "WARNING",
  ERROR = "ERROR",
  PINK = "PINK",
  ORANGE = "ORANGE",
  YELLOW = "YELLOW",
  LIME = "LIME",
  BLUE = "BLUE",
  PURPLE = "PURPLE",
  TEAL = "TEAL",
}

export enum ChipSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export interface ChipProps {
  label?: string | number;
  variant?: ChipVariant;
  size?: ChipSize;
  svg?: React.ReactNode;
  icon?: PhosphorIcon;
  onDismiss?: () => void;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  isPill?: boolean;
  noBackground?: boolean;
  isLoading?: boolean;
  isEllipsis?: boolean;
}

const StyledChip = withTheme(styled.div<
  PropsWithTheme<{
    $variant: ChipVariant;
    $size: ChipSize;
    $isPill?: boolean;
    $isClickable?: boolean;
    $noBackground?: boolean;
  }>
>`
  display: flex;
  flex-shrink: 0;
  gap: 4px;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 16px;
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "inherit")};

  height: ${({ $size }) => {
    return match($size)
      .with(ChipSize.SMALL, () => "18px")
      .with(ChipSize.MEDIUM, () => "20px")
      .with(ChipSize.LARGE, () => "24px")
      .exhaustive();
  }};

  padding: ${({ $size }) => {
    return match($size)
      .with(ChipSize.SMALL, () => "0 4px")
      .with(ChipSize.MEDIUM, () => "0 6px")
      .with(ChipSize.LARGE, () => "0 8px")
      .exhaustive();
  }};

  background-color: ${({ theme, $variant, $noBackground }) => {
    if ($noBackground) {
      return "transparent";
    }

    return match($variant)
      .with(ChipVariant.PRIMARY, () => theme.color.background.primary)
      .with(ChipVariant.PRIMARY_ALT, () => theme.color.background.primaryAlt)
      .with(ChipVariant.SECONDARY, () => theme.color.background.secondary)
      .with(ChipVariant.TERTIARY, () => theme.color.background.tertiary)
      .with(ChipVariant.SUCCESS, () => theme.color.background.success)
      .with(ChipVariant.WARNING, () => theme.color.background.warning)
      .with(ChipVariant.ERROR, () => theme.color.background.error)
      .with(ChipVariant.PINK, () => theme.color.background.pink)
      .with(ChipVariant.ORANGE, () => theme.color.background.orange)
      .with(ChipVariant.YELLOW, () => theme.color.background.yellow)
      .with(ChipVariant.LIME, () => theme.color.background.lime)
      .with(ChipVariant.BLUE, () => theme.color.background.blue)
      .with(ChipVariant.PURPLE, () => theme.color.background.purple)
      .with(ChipVariant.TEAL, () => theme.color.background.teal)
      .exhaustive();
  }};
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};
  border-radius: ${({ $isPill }) => ($isPill ? "200px" : "4px")};
`);

const CHIP_SIZE_TO_SHIMMER_HEIGHT: Record<ChipSize, number> = {
  [ChipSize.SMALL]: 10,
  [ChipSize.MEDIUM]: 12,
  [ChipSize.LARGE]: 14,
};

const CHIP_SIZE_TO_SHIMMER_WIDTH: Record<ChipSize, number> = {
  [ChipSize.SMALL]: 14,
  [ChipSize.MEDIUM]: 18,
  [ChipSize.LARGE]: 22,
};

const CHIP_SIZE_TO_ICON_SIZE: Record<ChipSize, number> = {
  [ChipSize.SMALL]: 10,
  [ChipSize.MEDIUM]: 12,
  [ChipSize.LARGE]: 14,
};

const CHIP_VARIANT_TO_ICON_VARIANT: Record<ChipVariant, IconVariant> = {
  [ChipVariant.PRIMARY]: IconVariant.PRIMARY,
  [ChipVariant.PRIMARY_ALT]: IconVariant.PRIMARY_ALT,
  [ChipVariant.SECONDARY]: IconVariant.SECONDARY,
  [ChipVariant.TERTIARY]: IconVariant.TERTIARY,
  [ChipVariant.SUCCESS]: IconVariant.SUCCESS,
  [ChipVariant.WARNING]: IconVariant.WARNING,
  [ChipVariant.ERROR]: IconVariant.ERROR,
  [ChipVariant.PINK]: IconVariant.PINK,
  [ChipVariant.ORANGE]: IconVariant.ORANGE,
  [ChipVariant.YELLOW]: IconVariant.YELLOW,
  [ChipVariant.LIME]: IconVariant.LIME,
  [ChipVariant.BLUE]: IconVariant.BLUE,
  [ChipVariant.PURPLE]: IconVariant.PURPLE,
  [ChipVariant.TEAL]: IconVariant.TEAL,
};

const CHIP_VARIANT_TO_TEXT_VARIANT: Record<ChipVariant, TextVariant> = {
  [ChipVariant.PRIMARY]: TextVariant.PRIMARY,
  [ChipVariant.PRIMARY_ALT]: TextVariant.PRIMARY_ALT,
  [ChipVariant.SECONDARY]: TextVariant.PRIMARY,
  [ChipVariant.TERTIARY]: TextVariant.PRIMARY,
  [ChipVariant.SUCCESS]: TextVariant.SUCCESS,
  [ChipVariant.WARNING]: TextVariant.WARNING,
  [ChipVariant.ERROR]: TextVariant.ERROR,
  [ChipVariant.PINK]: TextVariant.PINK,
  [ChipVariant.ORANGE]: TextVariant.ORANGE,
  [ChipVariant.YELLOW]: TextVariant.YELLOW,
  [ChipVariant.LIME]: TextVariant.LIME,
  [ChipVariant.BLUE]: TextVariant.BLUE,
  [ChipVariant.PURPLE]: TextVariant.PURPLE,
  [ChipVariant.TEAL]: TextVariant.TEAL,
};

const CHIP_SIZE_TO_TEXT_SIZE: Record<ChipSize, TextSize> = {
  [ChipSize.SMALL]: TextSize.BODY_SM,
  [ChipSize.MEDIUM]: TextSize.BODY_MD,
  [ChipSize.LARGE]: TextSize.BODY_LG,
};
const Chip = ({
  label,
  variant = ChipVariant.SECONDARY,
  size = ChipSize.MEDIUM,
  svg,
  icon,
  onDismiss,
  onClick,
  isPill,
  noBackground,
  isLoading,
  isEllipsis,
}: ChipProps) => {
  if (!label && !icon) {
    return null;
  }

  if (isLoading) {
    return (
      <StyledChip
        $variant={variant}
        $size={size}
        $isPill={isPill}
        $isClickable={false}
        $noBackground={noBackground}
      >
        {(icon || svg) && (
          <FlexItem shrink={0} grow={0}>
            <AvatarShimmer size={CHIP_SIZE_TO_ICON_SIZE[size]} />
          </FlexItem>
        )}
        <FlexItem shrink={0} grow={0}>
          <TextShimmer
            height={CHIP_SIZE_TO_SHIMMER_HEIGHT[size]}
            width={CHIP_SIZE_TO_SHIMMER_WIDTH[size]}
          />
        </FlexItem>
      </StyledChip>
    );
  }

  return (
    <StyledChip
      $variant={variant}
      $size={size}
      $isPill={isPill}
      $isClickable={!!onClick}
      $noBackground={noBackground}
      onClick={onClick}
    >
      {icon && (
        <FlexItem shrink={0} grow={0}>
          <Icon
            component={icon}
            size={CHIP_SIZE_TO_ICON_SIZE[size]}
            variant={CHIP_VARIANT_TO_ICON_VARIANT[variant]}
          />
        </FlexItem>
      )}
      {svg && (
        <FlexItem shrink={0} grow={0}>
          {svg}
        </FlexItem>
      )}
      {label && (
        <FlexItem shrink={0} grow={0}>
          <Text variant={CHIP_VARIANT_TO_TEXT_VARIANT[variant]} size={CHIP_SIZE_TO_TEXT_SIZE[size]}>
            {label}
            {isEllipsis && <Ellipsis isPulse />}
          </Text>
        </FlexItem>
      )}
      {onDismiss && (
        <FlexWrapper onClick={onDismiss}>
          <Icon component={XIcon} size={12} variant={IconVariant.TERTIARY} />
        </FlexWrapper>
      )}
    </StyledChip>
  );
};

export default Chip;
